import {
  Component,
  DestroyRef,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
  effect,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Router } from '@angular/router';
import { AuthService } from 'app/core/auth/auth.service';
import { ScreenLockService } from 'app/core/services/screen-lock.service';
import { UserService } from 'app/core/user/user.service';
import { finalize } from 'rxjs';

@Component({
  selector: 'app-screen-lock-overlay',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: './screen-lock-overlay.component.html',
  styleUrl: './screen-lock-overlay.component.scss',
})
export class ScreenLockOverlayComponent implements OnInit, OnDestroy {
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _fb = inject(FormBuilder);
  private readonly _auth = inject(AuthService);
  private readonly _screenLock = inject(ScreenLockService);
  private readonly _userService = inject(UserService);
  private readonly _router = inject(Router);

  @ViewChild('passwordInput') passwordInput?: ElementRef<HTMLInputElement>;

  readonly locked = this._screenLock.locked;
  readonly unlocking = signal(false);
  readonly showPassword = signal(false);
  readonly errorMessage = signal<string | null>(null);

  displayName = '';

  readonly form = this._fb.nonNullable.group({
    password: ['', Validators.required],
  });

  constructor() {
    effect(() => {
      if (!this.locked()) return;
      queueMicrotask(() => this.passwordInput?.nativeElement?.focus());
    });
  }

  ngOnInit(): void {
    this._screenLock.start();

    this._userService.user$
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((user) => {
        this.displayName =
          user?.fullName ||
          user?.name ||
          [user?.firstName, user?.lastName].filter(Boolean).join(' ') ||
          user?.email ||
          this._auth.profile()?.name ||
          this._auth.profile()?.userName ||
          'User';
      });
  }

  ngOnDestroy(): void {
    this._screenLock.stop();
  }

  unlock(): void {
    if (this.form.invalid || this.unlocking()) {
      this.form.markAllAsTouched();
      return;
    }

    this.unlocking.set(true);
    this.errorMessage.set(null);
    this.form.disable();

    const password = this.form.controls.password.value;

    this._auth
      .verifyPassword(password)
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        finalize(() => {
          this.unlocking.set(false);
          this.form.enable();
        })
      )
      .subscribe({
        next: () => {
          this.form.reset({ password: '' });
          this.errorMessage.set(null);
          this._screenLock.unlock();
        },
        error: (err: unknown) => {
          this.form.controls.password.setValue('');
          const httpErr = err instanceof HttpErrorResponse ? err : null;
          this.errorMessage.set(
            httpErr?.error?.message ||
              httpErr?.error?.Message ||
              'Invalid password'
          );
          queueMicrotask(() => this.passwordInput?.nativeElement?.focus());
        },
      });
  }

  signOut(): void {
    this._auth
      .signOut()
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(() => {
        this._router.navigate(['/sign-in']);
      });
  }
}
